import React, { useState, useEffect } from 'react';
import { RiExchangeDollarFill } from "react-icons/ri";
import { BsCartX } from "react-icons/bs";
import { GrCube } from "react-icons/gr";
import { MdOutlineTrain } from "react-icons/md";
import { LuClipboardX } from "react-icons/lu";
import { LuClock } from "react-icons/lu";
import { LuClipboardCheck } from "react-icons/lu";
import { RiInbox2Line } from "react-icons/ri";
import { TbTruckReturn } from "react-icons/tb";
import { Link } from 'react-router-dom';
import InvoiceDetailPopup from '../Invoice/InvoiceDetail.js';

const OrderReturnList = () => {
  const [orders, setOrders] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortedOrders, setSortedOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [dateSortOrder, setDateSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Invoice Modal States
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // Image Modal States
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const [stats, setStats] = useState({
    totalReturns: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, startDate, endDate, dateFilterType, dateSortOrder]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [ordersResponse, sellersResponse, usersResponse] = await Promise.all([
        fetch('https://cyberathon.com/multivendor/index.php/Api/Master_controller/list_order_return'),
        fetch('https://cyberathon.com/multivendor/index.php/Api/Master_controller/list_seller_registration'),
        fetch('https://cyberathon.com/multivendor/index.php/Api/Master_controller/user_list')
      ]);

      const ordersData = await ordersResponse.json();
      const sellersData = await sellersResponse.json();
      const usersData = await usersResponse.json();

      if (ordersData.status === "true" && ordersData.data) {
        setOrders(ordersData.data);
      } else {
        setError('No return data found');
      }

      if (sellersData.status && sellersData.data) {
        setSellers(sellersData.data);
      }

      if (usersData.status && usersData.data) {
        setUsers(usersData.data);
      }

    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData) => {
    const stats = {
      totalReturns: ordersData.length
    };
    setStats(stats);
  };

  const getBusinessName = (sellerId) => {
    const seller = sellers.find(s => s.seller_id === sellerId?.toString());
    return seller ? seller.business_name : 'N/A';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.user_id === userId?.toString());
    return user ? user.name : 'N/A';
  };

  const filterByDateRange = (ordersData) => {
    if (dateFilterType === 'all') {
      return ordersData;
    }

    const now = new Date();
    let start, end;

    switch (dateFilterType) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7));
        end = new Date();
        break;
      
      case 'month':
        start = new Date(now.setMonth(now.getMonth() - 1));
        end = new Date();
        break;
      
      case 'custom':
        if (!startDate || !endDate) return ordersData;
        start = new Date(startDate);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      
      default:
        return ordersData;
    }

    return ordersData.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= start && orderDate <= end;
    });
  };

  const applyFilters = () => {
    let filtered = [...orders];

    // Filter only "Return" status orders
    filtered = filtered.filter(order => order.order_status?.toLowerCase() === 'return');

    // Apply date filter
    filtered = filterByDateRange(filtered);

    // Apply date sorting
    filtered = sortByDate(filtered);

    setSortedOrders(filtered);
    calculateStats(filtered);
    setCurrentPage(1);
  };

  const sortByDate = (ordersData) => {
    return [...ordersData].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (dateSortOrder === 'asc') {
        return dateA - dateB; // Oldest first
      } else {
        return dateB - dateA; // Newest first
      }
    });
  };

  const toggleDateSort = () => {
    setDateSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleDateFilterChange = (type) => {
    setDateFilterType(type);
    if (type !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleCustomDateFilter = () => {
    if (startDate && endDate) {
      setDateFilterType('custom');
    }
  };

  const clearDateFilter = () => {
    setDateFilterType('all');
    setStartDate('');
    setEndDate('');
  };

  const getOrderStatusBadge = (status) => {
    const s = status?.toLowerCase();

    if (s === "return") {
      return "badge border border-warning text-warning";
    } else if (s === "cancel") {
      return "badge border border-danger text-danger";
    }
    return "badge border border-dark text-dark";
  };

  const getPaymentStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'paid' || statusLower === 'completed') {
      return 'badge bg-success text-light px-2 py-1 fs-13';
    } else if (statusLower === 'pending') {
      return 'badge bg-light text-dark px-2 py-1 fs-13';
    } else if (statusLower === 'refund') {
      return 'badge bg-light text-dark px-2 py-1 fs-13';
    }
    return 'badge bg-secondary text-light px-2 py-1 fs-13';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleShowInvoice = (orderId) => {
    setSelectedOrderId(orderId);
    setShowInvoiceModal(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoiceModal(false);
    setSelectedOrderId(null);
  };

  const handleShowImages = (images) => {
    if (images && images.length > 0) {
      setSelectedImages(images);
      setShowImageModal(true);
    }
  };

  const handleCloseImages = () => {
    setShowImageModal(false);
    setSelectedImages([]);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, currentPage + 1);

    if (currentPage === 1) {
      endPage = Math.min(3, totalPages);
    }

    if (currentPage === totalPages) {
      startPage = Math.max(totalPages - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box">
              <h4 className="mb-0 fw-semibold">Order Returns</h4>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="javascript: void(0);">Orders</a>
                </li>
                <li className="breadcrumb-item active">Return List</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">Quick Date Filter</label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('all')}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'today' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('today')}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('week')}
                      >
                        Last 7 Days
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('month')}
                      >
                        Last 30 Days
                      </button>
                    </div>
                  </div>
                  
                  <div className="col-md-2">
                    <label className="form-label">Sort by Date</label>
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={toggleDateSort}
                    >
                      <i className={`fa-solid fa-arrow-${dateSortOrder === 'desc' ? 'down' : 'up'} me-2`}></i>
                      {dateSortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                    </button>
                  </div>
                  
                  <div className="col-md-2">
                    <label htmlFor="startDate" className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="col-md-2">
                    <label htmlFor="endDate" className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleCustomDateFilter}
                      disabled={!startDate || !endDate}
                    >
                      <i className="fa-solid fa-filter me-1"></i>
                      Apply Custom Range
                    </button>
                    {(dateFilterType !== 'all' || startDate || endDate) && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearDateFilter}
                      >
                        <i className="fa-solid fa-times me-1"></i>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                
                {dateFilterType !== 'all' && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="fa-solid fa-info-circle me-1"></i>
                      Showing orders from: <strong>
                        {dateFilterType === 'today' && 'Today'}
                        {dateFilterType === 'week' && 'Last 7 days'}
                        {dateFilterType === 'month' && 'Last 30 days'}
                        {dateFilterType === 'custom' && startDate && endDate && 
                          `${formatDate(startDate)} to ${formatDate(endDate)}`}
                      </strong>
                    </small>
                  </div>
                )}
              </div>
              
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h4 className="card-title mb-2">Order Returns</h4>
                      <p className="text-muted fw-medium fs-22 mb-0">{stats.totalReturns}</p>
                    </div>
                    <div>
                      <div className="avatar-md bg-warning bg-opacity-10 rounded">
                        <TbTruckReturn className="fs-32 text-warning avatar-title" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="d-flex card-header justify-content-between align-items-center">
                <div>
                  <h4 className="card-title">All Returns List</h4>
                </div>
                <div className="dropdown">
                  <a href="#" className="dropdown-toggle btn btn-sm btn-outline-light rounded" data-bs-toggle="dropdown" aria-expanded="false">
                    This Month
                  </a>
                  <div className="dropdown-menu dropdown-menu-end">
                    <a href="#!" className="dropdown-item">Download</a>
                    <a href="#!" className="dropdown-item">Export</a>
                    <a href="#!" className="dropdown-item">Import</a>
                  </div>
                </div>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger m-3" role="alert">
                    {error}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 table-hover table-centered">
                      <thead className="bg-light-subtle">
                        <tr>
                          <th>Return ID</th>
                          <th>Order ID</th>
                          <th>Created at</th>
                          <th>Customer</th>
                          <th>Seller</th>
                          <th>Reason</th>
                          <th>Comments</th>
                          <th>Payment Status</th>
                          <th>Payment Mode</th>
                          <th>Order Status</th>
                          <th>Images</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.length > 0 ? (
                          currentOrders.map((order) => (
                            <tr key={order.return_id}>
                              <td>#{order.return_id}</td>
                              <td>#{order.order_id}</td>
                              <td>{formatDate(order.created_at)}</td>
                              <td>{getUserName(order.user_id)}</td>
                              <td>{getBusinessName(order.seller_id)}</td>
                              <td>{order.reason_for_return || 'N/A'}</td>
                              <td>{order.comments || 'N/A'}</td>
                              <td>
                                <span className={getPaymentStatusBadge(order.payment_status)}>
                                  {order.payment_status}
                                </span>
                              </td>
                              <td>{order.payment_mode}</td>
                              <td>
                                <span className={getOrderStatusBadge(order.order_status)}>
                                  {order.order_status}
                                </span>
                              </td>
                              <td>
                                {order.multi_image && order.multi_image.length > 0 ? (
                                  <button
                                    onClick={() => handleShowImages(order.multi_image)}
                                    className="btn btn-soft-info btn-sm"
                                    title="View Images"
                                  >
                                    <i className="fa-solid fa-images align-middle fs-18"></i>
                                    <span className="ms-1 badge bg-info">{order.multi_image.length}</span>
                                  </button>
                                ) : (
                                  <span className="text-muted">No Images</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => handleShowInvoice(order.order_id)}
                                    className="btn btn-soft-success btn-sm"
                                    title="View Invoice"
                                  >
                                    <i className="fa-solid fa-file-invoice align-middle fs-18"></i>
                                  </button>
                                  <Link to={`/order-list-edit/${order.order_id}`} className="btn btn-soft-primary btn-sm">
                                    <i className="fa-solid fa-pencil align-middle fs-18"></i>
                                  </Link>
                                  <Link className="btn btn-soft-danger btn-sm">
                                    <i className="fa-solid fa-trash align-middle fs-18"></i>
                                  </Link>
                                  <Link
                                    to={`/order-detail-view/${order.order_id}`}
                                    className="btn btn-light btn-sm"
                                  >
                                    <i className="fa-solid fa-eye fs-18"></i>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="12" className="text-center py-4">
                              No returns found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="card-footer border-top">
                <nav aria-label="Page navigation example">
                  <ul className="pagination justify-content-end mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={handlePrev}>Previous</button>
                    </li>
                    {getPageNumbers().map((page) => (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)}>{page}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={handleNext}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Detail Popup */}
      <InvoiceDetailPopup
        show={showInvoiceModal}
        onClose={handleCloseInvoice}
        orderId={selectedOrderId}
      />

      {/* Image Modal */}
      {showImageModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleCloseImages}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Return Product Images</h5>
                <button type="button" className="btn-close" onClick={handleCloseImages}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="col-md-6">
                      <div className="border rounded p-2">
                        <img
                          src={`https://cyberathon.com/multivendor/${image}`}
                          alt={`Return image ${index + 1}`}
                          className="img-fluid rounded"
                          style={{ width: '100%', height: '300px', objectFit: 'contain' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseImages}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 text-center">
              © Venton. Crafted by{" "}
              <iconify-icon icon="iconamoon:heart-duotone" className="fs-18 align-middle text-danger" />{" "}
              <a href="" className="fw-bold footer-text" target="_blank">Techzaa</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OrderReturnList;